/**
 * The table every screen in this app uses.
 *
 * FULL WIDTH, STICKY HEADER, SCROLLS IN ITS OWN BOX. Three properties, and the
 * third is the one that is easy to get wrong: a fourteen-column table must
 * scroll horizontally INSIDE its container, never make the page body scroll —
 * otherwise the sidebar and the header slide off the left of the screen when a
 * reader reaches for the last column.
 *
 * It is deliberately not a data-grid. No virtualisation, no client-side sorting,
 * no column resizing: the register's rows come from Postgres in the order a view
 * defines, paging happens in SQL with a total ordering so pages are stable, and
 * sorting is a query parameter. A client-side sort over one page of 100 rows out
 * of 4,796 would order the page and look like it ordered the register — which is
 * the kind of quietly wrong that this project exists to avoid.
 *
 * The empty state is a first-class case. A filter matching nothing is a real
 * answer and must not look like a loading failure.
 */
import { useLayoutEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { ArrowDown, ArrowUp, Inbox } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

export interface Column<T> {
  /** Stable key. Also the sort key sent to the API when `sortable` is set. */
  key: string
  header: ReactNode
  /** Right-aligned, tabular numerals. Use for every number. */
  numeric?: boolean
  /** Hidden below the given breakpoint, for columns that are context not content. */
  hideBelow?: 'sm' | 'md' | 'lg' | 'xl'
  width?: string
  cell: (row: T) => ReactNode
  sortable?: boolean
  /**
   * The direction the FIRST click on this column asks for.
   *
   * Not every column's useful end is the top of the alphabet. A register
   * sorted oldest-signed-first opens on 2001 and buries everything anyone is
   * working on, and "most values" is the interesting end of a count. So each
   * column declares where it opens, and the toggle flips from there.
   * Defaults to ascending.
   */
  defaultDir?: SortDir
}

export type SortDir = 'asc' | 'desc'

const flip = (d: SortDir): SortDir => (d === 'asc' ? 'desc' : 'asc')

/** Breathing room under the last thing below the table. */
const GAP = 12

/**
 * The floor, only there to stop the box collapsing to nothing on a very short
 * window. Kept low deliberately: WHERE THIS FLOOR BINDS, THE FOOTER GOES BACK
 * OFF SCREEN, which is the bug this measurement exists to fix. At 220 it bound
 * on /renewals — the page with the most chrome above its table — and put the
 * footer 4px past the bottom of a 720px window.
 */
const MIN_BOX = 140

/**
 * How tall the scroll box may be, measured rather than assumed.
 *
 * It used to be `calc(100vh - 20rem)` — a guess at how much chrome sits above
 * the table. The guess is wrong whenever the real total differs, and it does:
 * the filter bar wraps from one row to two depending on width, the trust banner
 * is one line or two, and some screens carry a caveat and others do not. When
 * the guess is too generous the box runs past the bottom of the window and the
 * footer — the line saying how many of how many rows you are looking at — is
 * cut off until the reader scrolls the page, which is the bug this fixes.
 *
 * So: take the box's own distance from the top of the DOCUMENT, and give it
 * whatever is left. Document-relative, not viewport-relative, because
 * `getBoundingClientRect().top` moves as the page scrolls and a height that
 * depended on scroll position would grow every time the reader scrolled down.
 *
 * WHAT SITS BELOW IS MEASURED TOO, for the same reason. The row-count footer
 * and the note explaining the value column are the caller's siblings, and a
 * constant standing in for them was wrong the moment the note reflowed to a
 * different number of lines — it left the note clipped at the bottom of the
 * window. Measuring the distance from the box's bottom edge to the bottom of
 * its last sibling covers both, and their margins with them.
 *
 * That delta cannot feed back into the box height: it is a distance BETWEEN two
 * elements below the box, so making the box taller or shorter moves both ends
 * equally and the number does not change. Likewise, resizing the box changes
 * the page's height but not what sits above it, so the next measurement returns
 * the same value and React drops the no-op update.
 */
function useAvailableHeight(reserve?: number) {
  // A CALLBACK REF, not useRef, and the difference is the whole fix.
  //
  // DataTable returns a skeleton while the rows are still loading, so the box
  // does not exist on first render. A useRef effect keyed on [reserve] then
  // runs once against a null ref, finds nothing to measure, and never runs
  // again when the real table mounts — the table stayed uncapped and the
  // footer stayed off screen. Holding the node in state makes its arrival a
  // dependency, so the measurement happens when there is something to measure.
  const [box, setBox] = useState<HTMLDivElement | null>(null)
  const [height, setHeight] = useState<number | null>(null)

  // Layout effect, not effect: this runs before paint, so the table is never
  // painted at the wrong height and then snapped to the right one.
  useLayoutEffect(() => {
    const el = box
    if (!el) return
    const measure = () => {
      const box = el.getBoundingClientRect()
      const top = box.top + window.scrollY
      // Everything the caller renders after the table: the footer, the note,
      // and the gaps between them. `reserve` overrides it for a caller that
      // knows better.
      let below = reserve
      if (below == null) {
        // The bordered wrapper, whose siblings are the footer and the note.
        const wrapper = el.parentElement
        let last: Element | null = null
        for (let n = wrapper?.nextElementSibling ?? null; n; n = n.nextElementSibling) last = n
        below = last
          ? Math.max(0, last.getBoundingClientRect().bottom - box.bottom)
          : 0
      }
      // Whatever the page keeps below its last element — the content area's
      // bottom padding. Without it the document overflows by exactly that
      // padding and the window grows a scrollbar over empty space: nothing is
      // hidden, but the page scrolls for no reason.
      //
      // READ FROM THE ELEMENT THAT DECLARES IT, not inferred from scrollHeight.
      // It used to be `scrollHeight - (lastBottom + scrollY)`, which is only the
      // padding when the page overflows: `scrollHeight` bottoms out at the
      // VIEWPORT height, so on any page whose content does not fill the window
      // that expression counted every empty pixel below the content as padding
      // and squeezed the box down to MIN_BOX. Visible the moment a filter left
      // a table with two rows — a 130px scroll box under 300px of white space.
      //
      // Clamping the old expression instead would oscillate: with no padding
      // subtracted the box grows until the content exactly fills the window, the
      // real padding then pushes the page 24px past it, the next measurement
      // sees an overflowing page and shrinks the box back. Reading the padding
      // is height-independent, so there is nothing to feed back.
      const content = el.parentElement?.parentElement
      const pad = content ? parseFloat(getComputedStyle(content).paddingBottom) || 0 : 0
      setHeight(Math.max(MIN_BOX, Math.round(window.innerHeight - top - below - pad - GAP)))
    }
    measure()
    window.addEventListener('resize', measure)
    // Body, not the box: what changes the answer is layout ABOVE the table —
    // the filter bar wrapping to a second row, a caveat appearing. Observing
    // the box itself would miss all of it.
    const ro = new ResizeObserver(measure)
    ro.observe(document.body)
    return () => {
      window.removeEventListener('resize', measure)
      ro.disconnect()
    }
  }, [box, reserve])

  return [setBox, height] as const
}

export function DataTable<T>({
  columns,
  rows,
  keyOf,
  loading,
  emptyTitle = 'Nothing here',
  emptyBody,
  onRowClick,
  rowHref,
  sort,
  dir,
  onSort,
  caption,
  fixed,
  maxHeight,
  reserveBelow,
}: {
  columns: Column<T>[]
  rows: T[] | null
  /**
   * A stable, UNIQUE key per row.
   *
   * The index is passed as a second argument for one reason: sometimes the data
   * genuinely cannot distinguish two rows. `v_review_action` projects one row per
   * (metric_text, figure_kind) decision but exposes only metric_text, so
   * `discounted_price` — decided once as money and once as a percentage —
   * arrives twice, identical in every column the view returns except the note.
   * React then drops one, and a screen whose whole job is listing what people
   * decided silently omits a decision.
   *
   * Prefer a real identifier. Reach for the index only as a final tiebreaker, and
   * say why where you do.
   */
  keyOf: (row: T, index: number) => string
  loading?: boolean
  emptyTitle?: string
  emptyBody?: ReactNode
  onRowClick?: (row: T) => void
  /** Makes the whole row a link target while keeping it a real anchor per cell. */
  rowHref?: (row: T) => string
  sort?: string
  /**
   * The active direction. Omit it and the header behaves as a one-way sort —
   * which is what it used to do everywhere, and why a second click on the
   * active column appeared to do nothing: the click re-sent the key that was
   * already applied, so no state changed and no request was made.
   */
  dir?: SortDir
  onSort?: (key: string, dir: SortDir) => void
  caption?: ReactNode
  /**
   * Fixed column widths, taken from `Column.width`.
   *
   * WHY THIS EXISTS. A table sizes its columns to their CONTENT, and one cell
   * with `white-space: nowrap` — a filename, a path, a reference number — makes
   * its column as wide as the longest string in it. On the contracts table that
   * was a 120-character filename, so the first column consumed the entire width
   * and pushed the other ten into horizontal scroll. The table looked like it had
   * one column, and the report was "where did my columns go".
   *
   * Fixed layout takes widths from the header row instead, so a long value
   * truncates inside its column rather than resizing the table around it. Columns
   * hidden by `hideBelow` are `display: none` and consume nothing.
   */
  fixed?: boolean
  /**
   * An explicit cap, for the rare screen that wants one. Omit it — the default
   * is measured, and a literal value cannot know how much chrome is above the
   * table on this render at this width. See useAvailableHeight.
   *
   * Pass `"none"` to remove the cap entirely, which is what a DETAIL page wants:
   * the measured default assumes the table owns the space below it, so on a page
   * with sections underneath it caps every table to the room left over and each
   * one scrolls inside itself. On the customer page that showed 11 of 22
   * agreements and 2 of 4 money rows, with nothing to say the rest were there.
   */
  maxHeight?: string
  /**
   * Space kept clear beneath the box. Omit it — the default measures whatever
   * the caller actually rendered after the table, which a number cannot track.
   */
  reserveBelow?: number
}) {
  const [boxRef, measured] = useAvailableHeight(reserveBelow)
  // An explicit cap wins; otherwise use the measured space, and fall back to
  // no cap for the single render before the first measurement.
  const cap = maxHeight ?? (measured != null ? `${measured}px` : undefined)
  if (loading || rows === null) {
    return (
      <div className="flex flex-col gap-2 rounded-md border p-4">
        {Array.from({ length: 8 }, (_, i) => (
          <Skeleton key={i} className="h-6 w-full" />
        ))}
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <Empty className="rounded-md border">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Inbox />
          </EmptyMedia>
          <EmptyTitle>{emptyTitle}</EmptyTitle>
          {emptyBody && <EmptyDescription>{emptyBody}</EmptyDescription>}
        </EmptyHeader>
      </Empty>
    )
  }

  return (
    <div className="min-w-0 rounded-md border">
      {/* The scroll box and the sticky header's containing block must be the
          SAME element — see the note on Table's containerClassName. A separate
          wrapper here is what stopped the header freezing. */}
      <Table
        className={cn(fixed && 'table-fixed')}
        containerClassName="overflow-auto rounded-md"
        containerStyle={{ maxHeight: cap }}
        containerRef={boxRef}
      >
          {caption && <caption className="p-2 text-left text-xs text-muted-foreground">{caption}</caption>}
          <TableHeader className="sticky top-0 z-10 bg-muted">
            <TableRow>
              {columns.map((c) => {
                const active = sort === c.key
                // With no `dir` supplied the header is one-way, as it was
                // before: ascending, and a repeat click is a no-op.
                const activeDir: SortDir = dir ?? 'asc'
                return (
                  <TableHead
                    key={c.key}
                    style={c.width ? { width: c.width } : undefined}
                    className={cn(
                      'whitespace-nowrap text-xs font-semibold uppercase tracking-wide',
                      c.numeric && 'text-right',
                      c.hideBelow === 'sm' && 'hidden sm:table-cell',
                      c.hideBelow === 'md' && 'hidden md:table-cell',
                      c.hideBelow === 'lg' && 'hidden lg:table-cell',
                      c.hideBelow === 'xl' && 'hidden xl:table-cell',
                    )}
                    aria-sort={
                      active ? (activeDir === 'desc' ? 'descending' : 'ascending') : undefined
                    }
                  >
                    {c.sortable && onSort ? (
                      <button
                        type="button"
                        // Clicking the active column flips it; clicking another
                        // opens that column at whichever end is useful for it.
                        onClick={() =>
                          onSort(
                            c.key,
                            active ? flip(activeDir) : (c.defaultDir ?? 'asc'),
                          )
                        }
                        className={cn(
                          'inline-flex items-center gap-1 uppercase hover:text-foreground',
                          active ? 'text-foreground' : 'text-muted-foreground',
                        )}
                        title={
                          active
                            ? `Sorted ${activeDir === 'desc' ? 'descending' : 'ascending'} — click to reverse`
                            : 'Sort by this column'
                        }
                      >
                        {c.header}
                        {/* The arrow shows the direction that is APPLIED, not a
                            fixed glyph. It used to be a down arrow whenever the
                            column was active, so `signed` (newest first) and
                            `title` (A→Z) both claimed to be descending. */}
                        {active ? (
                          activeDir === 'desc' ? (
                            <ArrowDown className="size-3" />
                          ) : (
                            <ArrowUp className="size-3" />
                          )
                        ) : (
                          <ArrowUp className="size-3 opacity-30" />
                        )}
                      </button>
                    ) : (
                      c.header
                    )}
                  </TableHead>
                )
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, i) => {
              const clickable = Boolean(onRowClick || rowHref)
              return (
                <TableRow
                  key={keyOf(row, i)}
                  data-testid="data-row"
                  className={cn(clickable && 'cursor-pointer')}
                  // A row that opens something is a control, so it takes focus
                  // and answers Enter. The first of the two clicks that reach a
                  // value's evidence is this one.
                  tabIndex={clickable ? 0 : undefined}
                  role={clickable ? 'button' : undefined}
                  onClick={
                    onRowClick
                      ? () => onRowClick(row)
                      : rowHref
                        ? () => { location.assign(rowHref(row)) }
                        : undefined
                  }
                  onKeyDown={
                    clickable
                      ? (e) => {
                          if (e.key !== 'Enter' && e.key !== ' ') return
                          e.preventDefault()
                          if (onRowClick) onRowClick(row)
                          else if (rowHref) location.assign(rowHref(row))
                        }
                      : undefined
                  }
                >
                  {columns.map((c) => (
                    <TableCell
                      key={c.key}
                      className={cn(
                        'align-top',
                        // A FIXED COLUMN MUST CONTAIN ITS OWN TEXT. Two defaults
                        // conspire against that and both are overridden here:
                        //
                        //   TableCell ships `whitespace-nowrap`, so a long value
                        //   cannot wrap however many lines the cell would allow —
                        //   and `line-clamp-2` on the content then clamps nothing,
                        //   because there is only ever one line to clamp.
                        //
                        //   A td does not clip. So that unwrappable line ran
                        //   straight over the next column: "Year 2 PEDIDO
                        //   BRA-24-107" printed on top of "SUBTOTAL", and the two
                        //   were unreadable together.
                        //
                        // Only under `fixed`. Auto layout sizes a column to its
                        // content, so there is nothing to overflow, and clipping
                        // there would hide text with no ellipsis to show for it.
                        // cn() is tailwind-merge, so these beat the base classes
                        // deterministically rather than by luck of stylesheet
                        // order. A cell that genuinely must not break — a number,
                        // a date, a pill — asks for nowrap itself.
                        fixed && 'overflow-hidden whitespace-normal',
                        c.numeric && 'num',
                        c.hideBelow === 'sm' && 'hidden sm:table-cell',
                        c.hideBelow === 'md' && 'hidden md:table-cell',
                        c.hideBelow === 'lg' && 'hidden lg:table-cell',
                        c.hideBelow === 'xl' && 'hidden xl:table-cell',
                      )}
                    >
                      {c.cell(row)}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
    </div>
  )
}

/**
 * Row count and paging. Says which population it counted, always.
 *
 * "341 contracts" is not an answer on its own — 341 of what? The register holds
 * 318 in the Four Js portfolio and 23 outside it, and a count whose population is
 * a guess is how a portfolio total ends up overstated by 23 contracts.
 */
export function TableFooter({
  shown,
  total,
  population,
  offset,
  limit,
  onOffset,
}: {
  shown: number
  total: number
  population?: string
  offset: number
  limit: number
  onOffset: (n: number) => void
}) {
  const from = total === 0 ? 0 : offset + 1
  const to = offset + shown
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 pt-3 text-sm text-muted-foreground">
      <span>
        {from.toLocaleString('en-GB')}–{to.toLocaleString('en-GB')} of{' '}
        <strong className="font-semibold text-foreground">{total.toLocaleString('en-GB')}</strong>
        {population ? ` ${population}` : ''}
      </span>
      {total > limit && (
        <span className="flex items-center gap-2">
          <button
            type="button"
            className="rounded-md border px-2 py-1 disabled:opacity-40"
            disabled={offset === 0}
            onClick={() => onOffset(Math.max(0, offset - limit))}
          >
            Previous
          </button>
          <button
            type="button"
            className="rounded-md border px-2 py-1 disabled:opacity-40"
            disabled={to >= total}
            onClick={() => onOffset(offset + limit)}
          >
            Next
          </button>
        </span>
      )}
    </div>
  )
}

/** A screen's title block. Every screen has one; several carry a caveat. */
export function PageHeader({
  title,
  subtitle,
  caveat,
  actions,
}: {
  title: string
  subtitle?: ReactNode
  /** A statement about what this screen does NOT show. Rendered as a warning. */
  caveat?: ReactNode
  actions?: ReactNode
}) {
  return (
    <div className="mb-4 flex flex-col gap-2">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
          {subtitle && (
            <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{subtitle}</p>
          )}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
      {/* `ev-warn-*`, not `trust-warn-*`. The trust-* palette belongs to the app
          this component was lifted from; this app's index.css defines the
          evidence palette as `--ev-*` and exposes it as `--color-ev-*`, so the
          old class names produced an unstyled paragraph. */}
      {caveat && (
        <p
          className="max-w-4xl rounded-md border border-ev-warn-border bg-ev-warn-bg
                     px-3 py-2 text-sm text-ev-warn"
        >
          {caveat}
        </p>
      )}
    </div>
  )
}
